/**
 * Cliente HTTP — o único lugar do frontend que sabe que existe uma rede.
 *
 * Autenticação é cookie de sessão httpOnly, então não há token para guardar:
 * `credentials: 'include'` e o navegador cuida do resto. O que sobra para nós é
 * o CSRF, que o Django exige em toda escrita.
 *
 * A regra de ouro herdada do projeto R: **erro nunca vira silêncio**. Toda
 * resposta fora do 2xx vira `ErroApi` com a mensagem que o backend escreveu — o
 * `detail` de 400 do `pricing_views` existe justamente para ser lido por uma
 * pessoa, e engoli-lo para mostrar "algo deu errado" desperdiça o trabalho.
 */

const BASE = '/api'

export class ErroApi extends Error {
  readonly status: number
  readonly corpo: unknown

  constructor(status: number, mensagem: string, corpo?: unknown) {
    super(mensagem)
    this.name = 'ErroApi'
    this.status = status
    this.corpo = corpo
  }

  /** 401/403 — a sessão caiu ou o usuário não tem a permissão. */
  get naoAutorizado(): boolean {
    return this.status === 401 || this.status === 403
  }
}

/** Chamado quando o servidor diz que a sessão não vale mais. Ver `stores/sessao`. */
let aoPerderSessao: () => void = () => {}

export function registrarPerdaDeSessao(callback: () => void) {
  aoPerderSessao = callback
}

function lerCookie(nome: string): string | null {
  const alvo = `${nome}=`
  for (const bruto of document.cookie.split(';')) {
    const item = bruto.trim()
    if (item.startsWith(alvo)) return decodeURIComponent(item.slice(alvo.length))
  }
  return null
}

/**
 * Garante o cookie CSRF antes da primeira escrita.
 *
 * O Django só semeia o cookie quando alguma view chama `get_token`. Num SPA que
 * abre direto na tela de login, o primeiro POST seria também a primeira
 * requisição — e falharia com 403 sem nada de errado nas credenciais.
 */
export async function semearCsrf(): Promise<void> {
  if (lerCookie('csrftoken')) return
  await fetch(`${BASE}/auth/csrf`, { credentials: 'include' })
}

const METODOS_SEGUROS = new Set(['GET', 'HEAD', 'OPTIONS'])

type Opcoes = {
  metodo?: string
  corpo?: unknown
  parametros?: Parametros
  sinal?: AbortSignal
}

export type Parametros = Record<string, string | number | boolean | string[] | null | undefined>

/**
 * Monta a query string.
 *
 * Array vira chave repetida (`?produtos=a&produtos=b`) — o `_lista` do backend
 * aceita essa forma e a separada por vírgula; repetir é a que sobrevive a um
 * valor que contenha vírgula.
 *
 * `null`/`undefined`/`''` são omitidos: filtro não preenchido não deve chegar
 * ao servidor como filtro vazio, que lá significa outra coisa.
 */
export function montarQuery(parametros: Parametros = {}): string {
  const busca = new URLSearchParams()
  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor === null || valor === undefined || valor === '') continue
    if (Array.isArray(valor)) {
      for (const item of valor) if (item !== '') busca.append(chave, item)
    } else {
      busca.append(chave, String(valor))
    }
  }
  const texto = busca.toString()
  return texto ? `?${texto}` : ''
}

async function extrairMensagem(resposta: Response): Promise<{ mensagem: string; corpo: unknown }> {
  const padrao = `Falha na requisição (HTTP ${resposta.status}).`
  const tipo = resposta.headers.get('content-type') ?? ''
  if (!tipo.includes('application/json')) return { mensagem: padrao, corpo: null }

  try {
    const corpo = await resposta.json()
    if (typeof corpo?.detail === 'string') return { mensagem: corpo.detail, corpo }
    // Erro de validação do DRF: `{campo: ["mensagem"]}`. Nomear o campo é o que
    // torna a mensagem acionável no formulário de de-para.
    if (corpo && typeof corpo === 'object') {
      const partes = Object.entries(corpo).map(
        ([campo, erros]) => `${campo}: ${Array.isArray(erros) ? erros.join(' ') : String(erros)}`,
      )
      if (partes.length) return { mensagem: partes.join(' · '), corpo }
    }
    return { mensagem: padrao, corpo }
  } catch {
    return { mensagem: padrao, corpo: null }
  }
}

export async function requisitar<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const metodo = (opcoes.metodo ?? 'GET').toUpperCase()
  const cabecalhos: Record<string, string> = { Accept: 'application/json' }

  if (!METODOS_SEGUROS.has(metodo)) {
    await semearCsrf()
    const token = lerCookie('csrftoken')
    if (token) cabecalhos['X-CSRFToken'] = token
  }

  // `FormData` monta o próprio `Content-Type` com o boundary do multipart.
  // Declarar um aqui quebraria o upload de planilha de forma difícil de achar.
  const ehFormulario = opcoes.corpo instanceof FormData
  if (opcoes.corpo !== undefined && !ehFormulario) cabecalhos['Content-Type'] = 'application/json'

  const resposta = await fetch(`${BASE}${caminho}${montarQuery(opcoes.parametros)}`, {
    method: metodo,
    credentials: 'include',
    headers: cabecalhos,
    signal: opcoes.sinal,
    body:
      opcoes.corpo === undefined
        ? undefined
        : ehFormulario
          ? (opcoes.corpo as FormData)
          : JSON.stringify(opcoes.corpo),
  })

  if (!resposta.ok) {
    const { mensagem, corpo } = await extrairMensagem(resposta)
    // 403 no `/auth/me` é sessão expirada; nas telas de de-para pode ser falta
    // de `pode_ingerir`. O store decide — aqui só avisamos.
    if (resposta.status === 401) aoPerderSessao()
    throw new ErroApi(resposta.status, mensagem, corpo)
  }

  if (resposta.status === 204) return undefined as T
  return (await resposta.json()) as T
}

/** Envelope de paginação do DRF. */
export type Pagina<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export const api = {
  obter: <T>(caminho: string, parametros?: Parametros, sinal?: AbortSignal) =>
    requisitar<T>(caminho, { parametros, sinal }),
  criar: <T>(caminho: string, corpo: unknown) => requisitar<T>(caminho, { metodo: 'POST', corpo }),
  atualizar: <T>(caminho: string, corpo: unknown) =>
    requisitar<T>(caminho, { metodo: 'PATCH', corpo }),
  remover: (caminho: string) => requisitar<void>(caminho, { metodo: 'DELETE' }),
}
